import { Clock } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { formatDate, formatDateTime, formatNumber } from "@/utils/format";
import {
  vigenciaValorEstaAberta,
  type LeadTimeValorCarradaFaixaDto,
} from "@/services/locacoes";

interface Props {
  open: boolean;
  contratoLabel?: string;
  faixas: LeadTimeValorCarradaFaixaDto[];
  onClose: () => void;
}

export function ModalLeadTimeValorCarrada({ open, contratoLabel, faixas, onClose }: Props) {
  const title = (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100">
        <Clock className="h-4 w-4 text-slate-700" />
      </div>
      <div>
        <span className="font-bold text-slate-900">Lead time — valores e carradas</span>
        {contratoLabel ? <p className="text-xs font-normal text-slate-500">{contratoLabel}</p> : null}
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="xl"
      footer={
        <div className="flex justify-end">
          <Button type="button" variant="secondary" onClick={onClose}>
            Fechar
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Cada faixa mostra o valor vigente no período e as carradas vinculadas a ele (pelo momento do
          cadastro).
        </p>
        {faixas.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-500">
            Nenhum histórico de valor encontrado para este contrato.
          </p>
        ) : (
          faixas.map((faixa) => {
            const vig = faixa.vigencia;
            const periodo = vigenciaValorEstaAberta(vig)
              ? `${formatDateTime(vig.vigenteDesde)} → agora`
              : `${formatDateTime(vig.vigenteDesde)} → ${formatDateTime(vig.vigenteAte || "")}`;
            return (
              <div key={vig.id} className="overflow-hidden rounded-lg border border-slate-200">
                <div className="flex flex-wrap items-start justify-between gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {faixa.quantidadeCarradas} carrada(s) · R$ {formatNumber(vig.valorUnitario, 2)}
                      {vigenciaValorEstaAberta(vig) ? (
                        <span className="ml-2 text-xs font-semibold text-emerald-700">Vigente</span>
                      ) : null}
                    </p>
                    <p className="text-xs text-slate-500">no período {periodo}</p>
                    {vig.observacao?.trim() ? (
                      <p className="mt-1 text-xs text-slate-600">Obs.: {vig.observacao.trim()}</p>
                    ) : null}
                    {vig.registradoPorNome ? (
                      <p className="text-xs text-slate-500">Registrado por {vig.registradoPorNome}</p>
                    ) : null}
                  </div>
                  <div className="text-right text-xs text-slate-600">
                    <p>{faixa.totalLancamentos} lançamento(s)</p>
                    <p className="font-medium text-slate-800">
                      Total R$ {formatNumber(faixa.valorTotal, 2)}
                    </p>
                  </div>
                </div>
                {faixa.carradas.length === 0 ? (
                  <p className="px-3 py-3 text-xs text-slate-500">
                    Nenhuma carrada vinculada a este valor neste período.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-xs">
                      <thead className="bg-white text-[10px] uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-3 py-2 font-semibold">Data da carrada</th>
                          <th className="px-3 py-2 font-semibold">Cadastro</th>
                          <th className="px-3 py-2 font-semibold">Qtd</th>
                          <th className="px-3 py-2 font-semibold">Material</th>
                          <th className="px-3 py-2 font-semibold">Operador</th>
                          <th className="px-3 py-2 font-semibold">Valor unit.</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {faixa.carradas.map((c) => (
                          <tr key={c.id}>
                            <td className="px-3 py-1.5 text-slate-700">{formatDate(c.data)}</td>
                            <td className="px-3 py-1.5 text-slate-600">{formatDateTime(c.horaRegistro)}</td>
                            <td className="px-3 py-1.5 text-slate-700">{c.quantidade}</td>
                            <td className="px-3 py-1.5 text-slate-700">{c.material || "—"}</td>
                            <td className="px-3 py-1.5 text-slate-700">{c.motorista || "—"}</td>
                            <td className="px-3 py-1.5 text-slate-700">
                              R$ {formatNumber(c.valorUnitario, 2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </Modal>
  );
}
